/**
 * MODULE: domain/pages/frontend/components/PageCommentsPanel.tsx
 * PURPOSE: Page discussion comments with comment-tier permission enforcement
 */

import {
  forwardRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@core/auth';
import { useWorkspaceMembers } from '@domain/workspace';
import { Button, Spinner, ToggleSwitch } from 'flowbite-react';
import { CommentMentionInput } from './CommentMentionInput';
import { PageCommentNotificationPrefs } from './PageCommentNotificationPrefs';
import { PageCommentThreadGroup } from './PageCommentThreadGroup';
import { usePagesShortcuts } from './PagesShortcutsModal';
import {
  useCreatePageComment,
  useDeletePageComment,
  usePageComments,
  usePageEffectivePermission,
  useResolvePageComment,
} from '../hooks/usePages';
import { useConvertCommentToTask, usePageCommentTasks } from '../hooks/usePageCommentTasks';
import {
  buildWorkspaceMemberMap,
  resolveMemberDisplayLabel,
} from '../utils/pageShareMemberUtils';
import { extractMentionedUserIds } from '../utils/commentMentionUtils';
import {
  filterPageLevelComments,
  isCommentResolved,
} from '../utils/blockCommentUtils';
import {
  buildCommentThreads,
  countOpenThreads,
  filterThreadsByResolvedVisibility,
  findCommentInThreads,
} from '../utils/pageCommentThreadUtils';
import {
  canCommentPage,
  canDeletePageComment,
  canResolvePageComment,
} from '../utils/pagePermissionUtils';
import { formatModShortcut } from '../utils/pageKeyboardShortcutsUtils';

type Props = {
  pageId: string;
  workspaceId: string;
  deepLinkCommentId?: string | null;
  onDeepLinkCommentConsumed?: () => void;
};

export type PageCommentsPanelHandle = {
  focusAndExpand: () => void;
};

export const PageCommentsPanel = forwardRef<PageCommentsPanelHandle, Props>(function PageCommentsPanel(
  { pageId, workspaceId, deepLinkCommentId, onDeepLinkCommentConsumed },
  ref
) {
  const sectionRef = useRef<HTMLElement>(null);
  const pendingFocusRef = useRef(false);
  const deepLinkOpenedRef = useRef<string | null>(null);
  const { registerHandlers } = usePagesShortcuts();
  const { user } = useAuth();
  const { data: effectivePermission } = usePageEffectivePermission(pageId);
  const { data: comments = [], isLoading, isError } = usePageComments(pageId);
  const { data: members = [] } = useWorkspaceMembers(workspaceId);
  const createComment = useCreatePageComment(pageId);
  const deleteComment = useDeletePageComment(pageId);
  const resolveComment = useResolvePageComment(pageId);
  const { taskByCommentId } = usePageCommentTasks(pageId);
  const convertCommentToTask = useConvertCommentToTask(pageId, workspaceId);
  const [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);

  const membersByUserId = useMemo(() => buildWorkspaceMemberMap(members), [members]);
  const pageLevelComments = useMemo(() => filterPageLevelComments(comments), [comments]);
  const commentThreads = useMemo(() => buildCommentThreads(pageLevelComments), [pageLevelComments]);
  const visibleThreads = useMemo(
    () => filterThreadsByResolvedVisibility(commentThreads, showResolved),
    [commentThreads, showResolved]
  );
  const openCount = useMemo(() => countOpenThreads(commentThreads), [commentThreads]);
  const canComment = canCommentPage(effectivePermission);
  const canResolve = canResolvePageComment(effectivePermission);
  const currentUserId = user?.id;

  const focusCommentsPanel = useCallback(() => {
    pendingFocusRef.current = true;
    setExpanded(true);
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  useEffect(() => {
    if (!expanded || !pendingFocusRef.current) {
      return;
    }
    pendingFocusRef.current = false;
    const timer = window.setTimeout(() => {
      const input = sectionRef.current?.querySelector('[data-testid="pages-comment-input"]');
      if (input instanceof HTMLTextAreaElement && !input.disabled) {
        input.focus();
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [expanded]);

  useImperativeHandle(ref, () => ({ focusAndExpand: focusCommentsPanel }), [focusCommentsPanel]);

  useEffect(() => {
    if (!isLoading && commentThreads.length > 0) {
      setExpanded(true);
    }
  }, [commentThreads.length, isLoading, pageId]);

  const handleSubmit = async () => {
    const content = draft.trim();
    if (!content) {
      return;
    }
    setExpanded(true);
    await createComment.mutateAsync({
      content,
      mentioned_user_ids: extractMentionedUserIds(content),
    });
    setDraft('');
  };

  const handleReply = async (parentCommentId: string, content: string) => {
    setReplyParentId(parentCommentId);
    try {
      await createComment.mutateAsync({
        content,
        mentioned_user_ids: extractMentionedUserIds(content),
        parent_comment_id: parentCommentId,
      });
    } finally {
      setReplyParentId(null);
    }
  };

  useEffect(() => {
    registerHandlers({ focusComments: focusCommentsPanel });
  }, [focusCommentsPanel, registerHandlers]);

  useEffect(() => {
    if (!deepLinkCommentId || isLoading) {
      return;
    }
    if (deepLinkOpenedRef.current === deepLinkCommentId) {
      return;
    }
    const target = findCommentInThreads(buildCommentThreads(filterPageLevelComments(comments)), deepLinkCommentId);
    if (!target) {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        if (params.has('block')) {
          return;
        }
      }
      onDeepLinkCommentConsumed?.();
      return;
    }
    if (target.block_id) {
      return;
    }
    deepLinkOpenedRef.current = deepLinkCommentId;
    setExpanded(true);
    if (isCommentResolved(target)) {
      setShowResolved(true);
    }
    setHighlightedCommentId(deepLinkCommentId);
    const highlightTimer = window.setTimeout(() => setHighlightedCommentId(null), 2400);
    const timer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const commentEl = sectionRef.current?.querySelector(
        `[data-testid="pages-comment-${deepLinkCommentId}"]`
      );
      if (commentEl instanceof HTMLElement) {
        commentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onDeepLinkCommentConsumed?.();
    }, 150);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(highlightTimer);
    };
  }, [comments, deepLinkCommentId, isLoading, onDeepLinkCommentConsumed]);

  useEffect(() => {
    deepLinkOpenedRef.current = null;
    setExpanded(false);
  }, [pageId]);

  return (
    <section
      ref={sectionRef}
      className={`pages-no-print border-t border-gray-100 px-8 dark:border-gray-800 ${expanded ? 'py-6' : 'py-3'}`}
      data-testid="pages-comments-panel"
      data-expanded={expanded ? 'true' : 'false'}
    >
      <button
        type="button"
        className={`flex w-full items-center justify-between text-left text-sm font-semibold text-gray-900 dark:text-white ${expanded ? 'mb-3' : ''}`}
        onClick={() => setExpanded((value) => !value)}
        data-testid="pages-comments-toggle"
      >
        <span>Comments ({openCount} open)</span>
        <span className="text-xs font-normal text-gray-500">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded ? (
        <>
          <details
            className="mb-4 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2 dark:border-gray-800 dark:bg-gray-900/20"
            data-testid="pages-comments-notification-settings"
          >
            <summary
              className="cursor-pointer select-none text-xs font-medium text-gray-700 dark:text-gray-300"
              data-testid="pages-comments-notification-settings-toggle"
            >
              @mention notification settings
            </summary>
            <div className="mt-3 space-y-2">
              <PageCommentNotificationPrefs nested />
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                <Link
                  to="/settings?tab=notifications"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                  data-testid="pages-comments-manage-notification-settings"
                >
                  Manage in Settings (Notifications)
                </Link>
              </p>
            </div>
          </details>

          {pageLevelComments.length > 0 ? (
            <div className="mb-3 flex items-center justify-end">
              <ToggleSwitch
                checked={showResolved}
                label="Show resolved"
                onChange={setShowResolved}
                data-testid="pages-comments-show-resolved"
              />
            </div>
          ) : null}

          {isLoading && (
            <div className="flex justify-center py-4">
              <Spinner size="sm" aria-label="Loading comments" />
            </div>
          )}
          {isError && <p className="text-sm text-red-600">Could not load comments.</p>}

          {!isLoading && !isError && visibleThreads.length === 0 && (
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400" data-testid="pages-comments-empty">
              {commentThreads.length === 0
                ? `No comments yet.${canComment ? ' Start the discussion below.' : ''}`
                : 'All comment threads are resolved. Toggle “Show resolved” to review them.'}
            </p>
          )}

          {!isLoading && !isError && visibleThreads.length > 0 && (
            <ul className="mb-4 space-y-4" data-testid="pages-comments-list">
              {visibleThreads.map((thread) => (
                <PageCommentThreadGroup
                  key={thread.root.id}
                  thread={thread}
                  authorLabelFor={(userId) => resolveMemberDisplayLabel(membersByUserId, userId)}
                  workspaceId={workspaceId}
                  pageId={pageId}
                  members={members}
                  currentUserId={currentUserId}
                  canComment={canComment}
                  canResolve={canResolve}
                  canDeleteComment={(comment) =>
                    canDeletePageComment(effectivePermission, comment.created_by, currentUserId)
                  }
                  onResolve={(commentId, resolved) =>
                    resolveComment.mutate({ commentId, resolved })
                  }
                  onDelete={(commentId) => deleteComment.mutate(commentId)}
                  onReply={handleReply}
                  replyPending={createComment.isPending && replyParentId === thread.root.id}
                  canCreateTask={canComment}
                  taskIdForComment={(commentId) => taskByCommentId.get(commentId)?.id ?? null}
                  onCreateTask={(comment) => convertCommentToTask.mutate(comment)}
                  createTaskPendingFor={(commentId) =>
                    convertCommentToTask.isPending && convertCommentToTask.variables?.id === commentId
                  }
                  resolvePending={resolveComment.isPending}
                  deletePending={deleteComment.isPending}
                  highlightedCommentId={highlightedCommentId}
                />
              ))}
            </ul>
          )}

          {canComment ? (
            <div className="space-y-2" data-testid="pages-comments-compose">
              <CommentMentionInput
                value={draft}
                onChange={setDraft}
                members={members}
                currentUserId={currentUserId}
                disabled={createComment.isPending}
                onSubmit={() => void handleSubmit()}
                submitDisabled={createComment.isPending || !draft.trim()}
              />
              <Button
                size="sm"
                color="blue"
                onClick={() => void handleSubmit()}
                disabled={createComment.isPending || !draft.trim()}
                data-testid="pages-comment-submit"
              >
                {createComment.isPending ? 'Posting…' : 'Post comment'}
              </Button>
              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                {formatModShortcut('Enter')} to post
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              You have view-only access — commenting is disabled.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
});
